import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ListFooter } from '../ui';

// Risk Items dashboard (Risk Analysis Build 2). Management sees every open risk
// across the tenant; owners see the ones they own. Each row shows which
// documents the risk is alerting on, with a Resolve action (owner or management).

const NAVY = '#2D2C72', INK = '#3B3A8F', RED = '#E22D2D', WARM = '#F7F6F3';
const APPROVER_ROLES = ['admin', 'management'];
const RATING_LABEL = { low: 'Low', med: 'Medium', high: 'High' };
const RATING_TONE = { low: '#0F7B3F', med: '#854F0B', high: RED };
const CAT_LABEL = {
  commercial: 'Commercial', material_fabric: 'Material — Fabric', material_trims: 'Material — Trims',
  capacity: 'Capacity / Delivery', technical: 'Technical / Quality',
};
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const inputStyle = { padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, background: '#fff' };

export default function RiskItems() {
  const { user } = useAuth();
  const isApprover = APPROVER_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('open');
  const [ownerId, setOwnerId] = useState('');
  const [owners, setOwners] = useState([]);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (status !== 'all') params.status = status;
    if (isApprover && ownerId) params.owner_id = ownerId;
    api.get('/risk/items', { params }).then((r) => setRows(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [status, ownerId, isApprover]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isApprover) api.get('/risk/owners').then((r) => setOwners(r.data.data || [])).catch(() => {}); }, [isApprover]);

  const resolve = async (it) => {
    const note = window.prompt(`Resolve "${CAT_LABEL[it.category] || it.category}" risk (${it.ra_number}).\nEnter a resolution note (required):`);
    if (note === null) return;
    if (!note.trim()) { alert('A resolution note is required.'); return; }
    try { await api.post(`/risk/items/${it.id}/resolve`, { resolution_note: note.trim() }); load(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const q = search.trim().toLowerCase();
  const visible = q ? rows.filter((r) => [r.ra_number, r.estimate_no, r.style_no, r.brand, r.gpo_no, r.owner_name].some((v) => String(v || '').toLowerCase().includes(q))) : rows;
  const openCount = rows.filter((r) => r.status === 'open').length;

  return (
    <div style={{ minHeight: '100vh', background: 'transparent' }}>
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0, color: NAVY, fontSize: 22 }}>⚠️ Risk Items</h2>
          <div style={{ color: '#777', fontSize: 13, marginTop: 4 }}>
            {isApprover ? 'All flagged risks across orders. ' : 'Risks assigned to you. '}
            <b style={{ color: openCount ? '#854F0B' : '#0F7B3F' }}>{openCount} open.</b>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
          {isApprover && (
            <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} style={{ ...inputStyle, minWidth: 200 }}>
              <option value="">All owners</option>
              {owners.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.role}</option>)}
            </select>
          )}
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search RA / estimate / style / GPO…" style={{ ...inputStyle, flex: '1 1 220px', minWidth: 200 }} />
        </div>

        <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>
                {['Risk', 'Rating', 'Order', 'Owner', 'Alerting on', 'Status', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#aaa' }}>Loading…</td></tr>}
              {!loading && !visible.length && <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#aaa' }}>No risk items{status === 'open' ? ' open' : ''}. 🎉</td></tr>}
              {!loading && visible.map((it) => {
                const canResolve = it.status === 'open' && (user?.id === it.owner_user_id || isApprover);
                return (
                  <tr key={it.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: NAVY }}>{CAT_LABEL[it.category] || it.category}<div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>{it.ra_number}</div></td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: RATING_TONE[it.rating] || '#555' }}>{RATING_LABEL[it.rating] || it.rating || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{it.estimate_no || '—'}{it.style_no ? <span style={{ color: '#999' }}> · {it.style_no}</span> : ''}{it.gpo_no ? <div style={{ fontSize: 11, color: '#999' }}>GPO {it.gpo_no}</div> : ''}</td>
                    <td style={{ padding: '10px 12px' }}>{it.owner_name || '—'}{it.owner_role ? <span style={{ color: '#999' }}> · {it.owner_role}</span> : ''}</td>
                    <td style={{ padding: '10px 12px', color: '#888', fontSize: 12 }}>{(Array.isArray(it.mapped_documents) ? it.mapped_documents : []).join(', ') || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {it.status === 'open'
                        ? <span style={{ color: '#854F0B', fontWeight: 700 }}>⏳ Open</span>
                        : <span style={{ color: '#0F7B3F', fontWeight: 700 }} title={`${it.resolution_note || ''} — ${it.resolved_by_name || ''} ${fmtDateTime(it.resolved_at)}`}>✓ Resolved</span>}
                      {it.status === 'resolved' && <div style={{ fontSize: 11, color: '#999' }}>{it.resolved_by_name || '—'} · {fmtDateTime(it.resolved_at)}</div>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {canResolve && (
                        <button onClick={() => resolve(it)} style={{ padding: '5px 14px', background: INK, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Resolve</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <ListFooter shown={visible.length} total={rows.length} noun="risk items" />
        </div>
      </div>
    </div>
  );
}
